//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
  PaginationState,
  VisibilityState,
  FilterFnOption,
  Row,
} from '@tanstack/react-table';
import { format, formatISO, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';

// Double Tap Support
import { DoubleTapRow } from '../components/DoubleTapRow';
import { SortableHeader } from '../components/SortableHeader';

// Styled Components
import {
  TrStyled,
  TableContainerStyled,
  SubContainerFiltersStyled,
  TdStyled,
  SubContainerHead,
  ShowFiltersButton,
  ArrowBottomStyled,
  DropDownInputStyledBig,
  SearchableDropdownStyledBig,
  ContainerButtonStyled,
  ContainerStyled,
  SubtitleStyled,
  ContainerLoader,
  FiltersWithLoadingStyled,
  StateCellStack,
} from './styles';
import {
  Th,
  Tbody,
  Title,
  Table,
  TextCell,
  MultiLineCell,
  TextHeader,
  InputRadioContainer,
  InputRadioStyled,
  InputCalendarStyled,
  MultiSelectDropdownStyled,
} from '../components/MainStyledComponents';

// Components
import { PkCell } from '../components/PkCell';
import { PaginationButtons } from '../components/PaginationButtons';
import InputExpedient from '../../../inputs/InputExpedient';
import InputText from '../../../inputs/InputText';
import InputIndex from '../../../inputs/InputIndex';
import InputPK from '../../../inputs/InputPK';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { SpecialTag } from '../../SpecialTag';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';
import { createFlagColumn, exportIgnoreProps } from '../components/flagColumn';
import { useFlagScopeTexts } from '../../../../hooks/useFlagScopeTexts';

// Types
import { columnID, getColumnNames, getColumnId } from '../components/types';
import { getTrackOptions } from '../../../../utils/catalogLabels';
import { tableProps, dataTable, flagRowType } from './types';
import {
  Options,
  onChangeValue,
} from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import {
  ImperativeRefSearchableDropdown,
  OptionSelected,
} from '../../../inputs/SearchableDropdown/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import {
  ImperativeRefInputExpedient,
  onChangeInputExpedientTypes,
} from '../../../inputs/InputExpedient/types';
import {
  ImperativeRefInputIndex,
  onChangeInputIndexTypes,
} from '../../../inputs/InputIndex/types';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import { variant, size } from '../../SpecialTag/types';
import { Order, OrderState, OrderType } from '../../../../types/orders';

// Mocks
import { mocksTable } from './mocks';

// Utils
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';

// Actions
import { setCursor } from '../../../../state/actions/ui/cursorTables';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
  useIsSmallDesktop,
} from '../../../../hooks/useMediaBreakPoints';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';
import IncludeInactiveToggle from '../../../inputs/IncludeInactiveToggle';
import {
  filterAndSortMultimediaUsers,
  filterAndSortOperators,
} from '../../../../utils/multimediaUserFilters';
import {
  generateRoidPadding,
  getStateOptionsByRoleAndEntityType,
} from '../../../../utils/order';
import { takeBase, TABLE_ENTITY_TYPES, tableIds, listingFiltersByRole } from '../types';
import { CLEARED_TABLE_FILTERS } from '../clearedTableFilters';
import { useServerPageSize } from '../utils/useServerPageSize';
import { useMirroredCursor } from '../utils/useMirroredCursor';

// Graphql
import { client } from '../../../../gql/client';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import {
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOLS_BY_IDS,
} from '../../../../gql/queries/orders/predefinedData';
import { option } from '../InputRadioArrowTable/types';
import { Loader } from '../../../inputs/Loader';
import { Role } from '../../../../types/auth';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';

// Columnas ordenables server-side → campo del backend (whitelist GenericOrder)
const SORTABLE_COLUMNS = {
  state: 'state',
  registerInitDate: 'registerInitDate',
  creationDate: 'creationDate',
  pkInit: 'pkInitKm',
  pkEnd: 'pkEndKm',
  track: 'track',
  direction: 'direction',
  // Solo se renderiza en el listado RV (showRouteNameColumn); el backend RV
  // tiene 'name' en SURVEILLANCE_ROUTE_SORTABLE_FIELDS.
  name: 'name',
  // Solo ordenable en RV (SURVEILLANCE_ROUTE_SORTABLE_FIELDS incluye
  // registerEndDate): la columna visual sigue siendo derivedEndDate — el
  // adapter RV mapea derivedEndDate = order.registerEndDate, misma cosa.
  // En tablas NO-RV la columna mantiene header plano (ver `columns`), así
  // que este mapeo solo entra en juego con tableId de RV.
  derivedEndDate: 'registerEndDate',
} as const;
const DEFAULT_SORT_FIELD = 'registerInitDate';

/**
 * Una fecha de celda, o «—» si no la hay o no es una fecha. 🔴 `format` lanza
 * «Invalid time value» con una fecha vacía, y un fallo en una celda tumbaba el
 * modal entero (QA 15-oct, T2.10+vacío: un recorrido pendiente de validar SIN
 * tramos no tiene fecha de registro ni final).
 */
const tableDate = (value: unknown): string => {
  if (!value) return '—';
  const date = new Date(value as string);
  return Number.isNaN(date.getTime())
    ? '—'
    : format(date, 'dd/MM/yyyy - HH:mm');
};
/**
 * Por defecto la bandera marca la Order de la fila: las filas de órdenes ya
 * llevan su id. Fuera del componente para que la referencia sea estable y no
 * rehaga las columnas en cada render.
 */
const defaultGetFlagOrderId = (row: flagRowType): string | undefined =>
  row.id || undefined;

export const InputRadioTagTable = ({
  titleText,
  data,
  onReFetchData,
  onReloadData,
  setRowState,
  onReFetchPagination,
  tableId,
  hiddenColumnIds,
  hiddenFilterIds,
  showRouteNameColumn,
  showRouteNameFilter,
  routeNameHeaderLabel,
  showEndDateFilter,
  showCreatorColumn,
  showOperatorsFilter,
  onRowDoubleTap,
  showButtonsPagination = true,
  hideFilterBar = false,
  fixedOrder = false,
  subtitle,
  showFlagColumn = false,
  showOnlyFlaggedFilter = false,
  getFlagOrderId = defaultGetFlagOrderId,
  selectableRowIds,
  excludedStateOptions,
  ...props
}: tableProps): JSX.Element => {
  // V4 FR-1.5 — helper para condicionar el render de cada filtro por id.
  const isFilterHidden = (id: string): boolean =>
    hiddenFilterIds?.includes(id) ?? false;
  // El filtro de Nombre sigue a la columna salvo que el consumidor lo separe
  // explícitamente (2026-08-11). Sin la prop, comportamiento idéntico al de
  // antes para los 3 consumidores que ya existían.
  const routeNameFilterVisible = showRouteNameFilter ?? showRouteNameColumn;
  const { t } = useTranslation(['tables', 'common', 'forms']);
  const flagScopeTexts = useFlagScopeTexts();
  const dispatch = useDispatch();
  const globalCursor = useSelector(getCursor(tableId as string), isEqual);

  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    description: string | undefined; // filtro por descripción (texto libre)
    take: number;
    cursor: string;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    teamIds: string[] | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    fromEndDate: string | undefined;
    toEndDate: string | undefined;
    classification: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat with other tables sharing this cursor shape)
    states: string[] | undefined; // multi-select (Plan 2 Tarea: filtro estado multi-select)
    userIds: string[] | undefined; // multi-select creador (entidad doble GenericOrder.orders[0].userId)
    operatorIds: string[] | undefined; // multi-select operarios (solo RV: SurveillanceRoute.userIDs)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador"
    track: string | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    routeName: string | undefined; // filtro "Nombre del recorrido" (solo RV, FR-1.5)
    roadId: string | undefined; // filtro "Carretera" (solo RV, FR-1.5)
    sortField: string | undefined;
    orderBy: 'asc' | 'desc' | undefined;
    onlyFlagged: true | undefined; // «Solo marcadas»: true o undefined, nunca false
  }>(globalCursor);

  const isLoadingData = useSelector(
    getIsLoadingData(tableId as string),
    isEqual
  );

  const dataAll = data?.genericOrders ?? mocksTable;
  const roleActive = data?.roleActive;
  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputListTeamRef = useRef<ImperativeRefMultiSelect>(null);
  const inputListCapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListSubcapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListClassificationRef =
    useRef<ImperativeRefSearchableDropdown>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListTrackRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListRoadRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputStateRef = useRef<ImperativeRefMultiSelect>(null);
  const inputUserRef = useRef<ImperativeRefMultiSelect>(null);
  const inputOperatorsRef = useRef<ImperativeRefMultiSelect>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputEndDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputExpedientRef = useRef<ImperativeRefInputExpedient>(null);
  const inputIndexRef = useRef<ImperativeRefInputIndex>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);
  const hasAutoSelectedConcession = useRef<boolean>(false);

  const isSmallDesktop = useIsSmallDesktop();
  const isLanscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  const showCapitolFilter = useFeatureFlag('filter_show_capitol', true);
  const showSubCapitolFilter = useFeatureFlag('filter_show_subcapitol', true);
  const showPredefinedFilter = useFeatureFlag('filter_show_predefined', true);

  const [expedientDefault, setExpedientDefault] = useState<number | undefined>(
    undefined
  );
  const [indexDefault, setIndexDefault] = useState<number | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
  // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
  const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
    tableId as string
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: takeBase,
  });

  const initialSubCapitolsOptions = data?.filtersDataOptions?.subCapitols?.map(
    (subCapitol: { name: string; id: string }) => ({
      label: subCapitol.name,
      value: subCapitol.id,
    })
  );

  const [subCapitolOptions, setSubCapitolOptions] = useState(
    initialSubCapitolsOptions
  );

  const [selectorData, setSelectorData] = useState<{
    description: string | undefined; // filtro por descripción (texto libre)
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    teamIds: string[] | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    fromEndDate: string | undefined;
    toEndDate: string | undefined;
    classification: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat)
    states: string[] | undefined; // multi-select
    userIds: string[] | undefined; // multi-select creador
    operatorIds: string[] | undefined; // multi-select operarios (solo RV)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador"
    track: string | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    routeName: string | undefined; // filtro "Nombre del recorrido" (solo RV, FR-1.5)
    roadId: string | undefined; // filtro "Carretera" (solo RV, FR-1.5)
  }>({
    description: undefined,
    indexType: undefined,
    indexExpedition: undefined,
    concessionId: undefined,
    teamIds: undefined,
    fromDate: undefined,
    toDate: undefined,
    fromEndDate: undefined,
    toEndDate: undefined,
    classification: undefined,
    capitolId: undefined,
    subCapitolId: undefined,
    predefinedOrderId: undefined,
    state: undefined,
    states: undefined,
    userIds: undefined,
    operatorIds: undefined,
    includeInactive: undefined,
    track: undefined,
    pkInitKm: undefined,
    pkInitMeter: undefined,
    pkEndKm: undefined,
    pkEndMeter: undefined,
    routeName: undefined,
    roadId: undefined,
  });

  // Tarea #11 — page-size selector. Activo en la lista principal de Work
  // Orders (`WORKORDER_GENERAL_VIEW`) y en el wizard Duplicar O.T.
  // (`DUPLICATE_GENERIC_ORDER`) — espejo del gate de InputRadioArrowTable,
  // que incluye DUPLICATE_COMMUNICATION en el wizard hermano. En la preview
  // del módulo Surveillance Route no llega tableId → `enabled=false` y
  // `takePagination` cae a `takeBase`.
  // `liveFilters: selectorData` para respetar filtros del form aún no
  // comprometidos a Redux al cambiar el take.
  const { takePagination, paginationProps } = useServerPageSize({
    tableId: tableId as string,
    // Tour P1 (2026-07-06): el RV ya es un listado principal server-side de
    // pleno derecho — selector activo también ahí (antes era stub/preview).
    // #74: Duplicar O.T. gana el footer de paginación completo.
    // Transfer básica→RV (2026-07-21): `SURVEILLANCE_ROUTE_MODAL` queda FUERA
    // a propósito — `enabled` es el gate del page-size selector, exclusivo de
    // los listados principales (los modales GENERIC_ORDER_MODAL/CAMPAIGN_MODAL
    // tampoco están, contrato de useServerPageSize). Las columnas/filtros del
    // dominio RV del modal ya llegan vía TABLE_ENTITY_TYPES (isSurveillanceTable)
    // + showRouteNameColumn; el take del modal lo fija el propio modal (±9,
    // clamp sobre el take=takeBase que dispatchean handleSearch/onClearValue
    // en contexto modal).
    enabled:
      tableId === tableIds.WORKORDER_GENERAL_VIEW ||
      tableId === tableIds.SURVEILLANCE_ROUTES ||
      tableId === tableIds.DUPLICATE_GENERIC_ORDER,
    selectorCursor,
    liveFilters: selectorData as unknown as Record<string, unknown>,
    setCurrentServerPage,
    onReFetchData,
  });

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  useEffect(() => {
    isLanscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLanscapeTablet]);

  const callbackSetRoid = useCallback(async () => {
    let classification;
    if (selectorCursor?.classification) {
      const classificationRaw = await client.query({
        query: GET_CLASSIFICATION_BY_ID_OR_ROID,
        variables: {
          id: selectorCursor?.classification,
        },
      });
      classification =
        classificationRaw?.data?.classification?.classificationCode;
    }
    const roidData = [
      '',
      selectorCursor?.indexType
        ? selectorCursor?.indexType >= 0
          ? generateRoidPadding(selectorCursor?.indexType, 4)
          : ''
        : '',
      selectorCursor?.indexExpedition
        ? selectorCursor?.indexExpedition >= 0
          ? generateRoidPadding(selectorCursor?.indexExpedition, 7)
          : ''
        : '',
      classification ? generateRoidPadding(classification, 2) : '',
    ];
    setExpedientDefault(
      selectorCursor?.indexExpedition !== undefined &&
        selectorCursor?.indexExpedition >= 0
        ? selectorCursor.indexExpedition
        : undefined
    );
    setIndexDefault(
      selectorCursor?.indexType !== undefined && selectorCursor?.indexType >= 0
        ? selectorCursor.indexType
        : undefined
    );
  }, [selectorCursor]);

  useEffect(() => {
    callbackSetRoid();
  }, [selectorCursor]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      predefinedOrder: showPredefinedFilter,
      capitol: showCapitolFilter,
      subCapitol: showSubCapitolFilter,
    }));
  }, [showPredefinedFilter, showCapitolFilter, showSubCapitolFilter]);

  useEffect(() => {
    const concessions = data?.filtersDataOptions?.concessions;
    const teams = data?.filtersDataOptions?.teams;
    setColumnVisibility((prev) => ({
      ...prev,
      concession: !concessions || concessions.length > 1,
      team: !!teams && teams.length > 0,
    }));
  }, [data?.filtersDataOptions?.concessions, data?.filtersDataOptions?.teams]);

  const [sizeTag, setSizeTag] = useState<size>(
    isPortraitTablet ? 'circle' : 'small'
  );

  useLayoutEffect(() => {
    setSizeTag(isPortraitTablet ? 'circle' : 'small');
  }, [isPortraitTablet]);

  const columnHelper = createColumnHelper<dataTable>();
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);

  // PR #27 (main 2026-06-XX): unificación de la ordenación por defecto a
  // `registerInitDate` en TODOS los módulos. Sustituye al fallback iter7
  // que solo aplicaba a SR (`creationDate` oculta → caía a registerInitDate
  // sólo allí).
  // 2026-07-17: Fecha final (derivedEndDate) ordenable SOLO en RV — el
  // backend solo whitelistea `registerEndDate` en
  // SURVEILLANCE_ROUTE_SORTABLE_FIELDS (en genéricas/campañas derivedEndDate
  // es derivado y NO ordenable).
  const isSurveillanceTable =
    TABLE_ENTITY_TYPES[tableId as string] === OrderType.SurveillanceRoute;
  // Ordenación server-side: campo activo y dirección derivados del cursor Redux
  const activeSortField = selectorCursor?.sortField ?? DEFAULT_SORT_FIELD;
  const activeSortColumnId =
    (Object.keys(SORTABLE_COLUMNS) as (keyof typeof SORTABLE_COLUMNS)[]).find(
      (k) => SORTABLE_COLUMNS[k] === activeSortField
    ) ?? 'registerInitDate';
  const sortDirection: 'asc' | 'desc' =
    selectorCursor?.orderBy === 'asc' ? 'asc' : 'desc';

  const onSortColumn = useCallback(
    (sortField: string) => {
      // Reutiliza activeSortField/sortDirection ya derivados del cursor (sin re-derivar).
      const isActive = activeSortField === sortField;
      const nextOrderBy: 'asc' | 'desc' =
        isActive && sortDirection === 'desc' ? 'asc' : 'desc';
      // Canonicalización de caché: el default (registerInitDate + desc) viaja
      // como sortField: undefined EXPLÍCITO — comparte partición con el flujo
      // legacy; el reducer hace MERGE y solo undefined explícito borra el campo.
      const isDefault =
        sortField === DEFAULT_SORT_FIELD && nextOrderBy === 'desc';
      const dataCursor = {
        sortField: isDefault ? undefined : sortField,
        orderBy: nextOrderBy,
        cursor: '', // volver a página 1 al reordenar
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setCurrentServerPage(1);
      onReFetchData?.();
    },
    [
      activeSortField,
      sortDirection,
      dispatch,
      tableId,
      setCurrentServerPage,
      onReFetchData,
    ]
  );

  // Helper que evita la duplicación ×7 del JSX de SortableHeader en las columnas ordenables.
  // Retorna una función `() => JSX` compatible con el `header` de columnHelper.
  // Declarado antes del useMemo de `columns` para que sea visible en sus deps.
  const sortableHeader = useCallback(
    (columnId: keyof typeof SORTABLE_COLUMNS) => () => {
      const label =
        translatedColumnNames[columnId as keyof typeof translatedColumnNames]
          ?.label ?? columnId;
      // Con el orden fijo, una flecha que no reordena engaña: cabecera plana.
      if (fixedOrder) {
        return (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {label}
          </TextHeader>
        );
      }
      return (
        <SortableHeader
          label={label}
          active={activeSortColumnId === columnId}
          direction={sortDirection}
          onToggle={() => onSortColumn(SORTABLE_COLUMNS[columnId])}
        />
      );
    },
    [
      translatedColumnNames,
      activeSortColumnId,
      sortDirection,
      onSortColumn,
      fixedOrder,
    ]
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: 'select',
          cell: ({ row }) => (
            <InputRadioContainer
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <InputRadioStyled
                name="inputRadio"
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </InputRadioContainer>
          ),
        }),
        // Banderitas (spec 2026-10-22 § 8.2): justo detrás de la selección y
        // solo si la pantalla la pide.
        ...(showFlagColumn
          ? [
              createFlagColumn<dataTable>(
                getFlagOrderId),
            ]
          : []),
        columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
          header: sortableHeader('state'),
          cell: (info) => {
            // Prevención P3 (2026-10-15): solo las filas de recorrido traen
            // `shiftWarning` (lo pone su adaptador); el resto de tablas no
            // cambian. Ámbar informativo como el aviso pendiente del tramo, y
            // siempre con texto (también en tablet vertical, donde el estado
            // es un círculo): la marca sin palabras no dice nada.
            const shiftWarning = (
              info.row.original as { shiftWarning?: string | null }
            ).shiftWarning;
            const stateTag = (
              <SpecialTag
                sizeTag={sizeTag}
                variant={info.getValue() as variant}
              />
            );
            // Solo los avisos que tienen texto: uno nuevo del servidor no puede
            // pintar la clave de traducción en crudo.
            if (
              shiftWarning !== 'paused-too-long' &&
              shiftWarning !== 'span-over-24h'
            )
              return stateTag;
            return (
              <StateCellStack>
                {stateTag}
                <SpecialTag
                  sizeTag="small"
                  variant={variant.PendingLink}
                  title={t(`pages:surveillance.shiftWarning.${shiftWarning}`)}
                  data-testid="route-shift-warning"
                />
              </StateCellStack>
            );
          },
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.roid.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size={'S'}>
              {formatRoidForDisplay(info.getValue())}
            </TextCell>
          ),
          filterFn: 'roidFilter' as FilterFnOption<Order>, //Indicate the filter function to be used for this column
        }),
        // V4 FR-1.6 — columna "Nom del recorregut" (solo visible si el caller
        // la activa con `showRouteNameColumn`). Tras ROID, antes de fecha.
        ...(showRouteNameColumn
          ? [
              columnHelper.accessor('name' as any, {
                id: 'name',
                header: () => {
                  const label =
                    routeNameHeaderLabel ??
                    t('pages:surveillance.name', 'Nom del recorregut');
                  if (fixedOrder) {
                    return (
                      <TextHeader size={'XS'} colorText={'greyDark'}>
                        {label}
                      </TextHeader>
                    );
                  }
                  return (
                    <SortableHeader
                      label={label}
                      active={activeSortColumnId === 'name'}
                      direction={sortDirection}
                      onToggle={() => onSortColumn(SORTABLE_COLUMNS.name)}
                    />
                  );
                },
                cell: (info) => (
                  <TextCell size={'S'}>
                    {(info.getValue() as string) || '—'}
                  </TextCell>
                ),
              }),
            ]
          : []),
        columnHelper.accessor(
          getColumnId(translatedColumnNames.registerInitDate.id),
          {
            header: sortableHeader('registerInitDate'),
            cell: (info) => (
              <TextCell size={'S'}>{tableDate(info.getValue())}</TextCell>
            ),
            filterFn: 'dateFilter' as FilterFnOption<dataTable>,
          }
        ),
        columnHelper.accessor(
          getColumnId(translatedColumnNames.derivedEndDate.id),
          {
            // Solo RV: header ordenable (sortField backend `registerEndDate`).
            // En el resto de tablas derivedEndDate NO está en la whitelist →
            // header plano como siempre.
            header: isSurveillanceTable
              ? sortableHeader('derivedEndDate')
              : () => (
                  <TextHeader size={'XS'} colorText={'greyDark'}>
                    {translatedColumnNames.derivedEndDate.label}
                  </TextHeader>
                ),
            cell: (info) => (
              <TextCell size={'S'}>{tableDate(info.getValue())}</TextCell>
            ),
            filterFn: 'dateFilter' as FilterFnOption<dataTable>,
          }
        ),

        columnHelper.accessor(getColumnId(translatedColumnNames.capitol.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.capitol.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size={'S'}>{info.getValue() as string}</TextCell>
          ),
        }),

        columnHelper.accessor(
          getColumnId(translatedColumnNames.subCapitol.id),
          {
            header: () => (
              <TextHeader size={'XS'} colorText={'greyDark'}>
                {translatedColumnNames.subCapitol.label}
              </TextHeader>
            ),
            cell: (info) => (
              <TextCell size={'S'}>{info.getValue() as string}</TextCell>
            ),
          }
        ),

        columnHelper.accessor(
          getColumnId(translatedColumnNames.predefinedOrder.id),
          {
            header: () => (
              <TextHeader size={'XS'} colorText={'greyDark'}>
                {translatedColumnNames.predefinedOrder.label}
              </TextHeader>
            ),
            cell: (info) => (
              <TextCell size={'S'}>{info.getValue() as string}</TextCell>
            ),
          }
        ),

        columnHelper.accessor(
          getColumnId(translatedColumnNames.concession.id),
          {
            header: () => (
              <TextHeader size={'XS'} colorText={'greyDark'}>
                {translatedColumnNames.concession.label}
              </TextHeader>
            ),
            cell: (info) => (
              <TextCell size={'S'}>{info.getValue() as string}</TextCell>
            ),
          }
        ),
        columnHelper.accessor(getColumnId(translatedColumnNames.team.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.team.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size={'S'}>{(info.getValue() as string) || ''}</TextCell>
          ),
        }),
        // Columna "Operarios" (solo RV, feedback usuario 2026-07-14): en RV la
        // tripulación va ligada al RECORRIDO, no a sus actuaciones — tras
        // Equipos. Gated por la misma prop que el filtro (showOperatorsFilter).
        ...(showOperatorsFilter
          ? [
              columnHelper.accessor('operators' as any, {
                id: 'operators',
                header: () => (
                  <TextHeader size={'XS'} colorText={'greyDark'}>
                    {t('forms:workOrder.operators')}
                  </TextHeader>
                ),
                cell: (info) => (
                  <TextCell size={'S'}>
                    {(info.getValue() as string) || '—'}
                  </TextCell>
                ),
              }),
            ]
          : []),
        // Columna "Creado por" (solo RV, 2026-07-17): userId de la Order gemela
        // resuelto a nombre por el adapter (row.creatorName). Mismo precedente
        // que showRouteNameColumn. Header plano — el creador NO está en la
        // whitelist de ordenación del backend.
        ...(showCreatorColumn
          ? [
              columnHelper.accessor('creatorName' as any, {
                id: 'creator',
                header: () => (
                  <TextHeader size={'XS'} colorText={'greyDark'}>
                    {t('common:labels.creator')}
                  </TextHeader>
                ),
                cell: (info) => (
                  <TextCell size={'S'}>
                    {(info.getValue() as string) || '—'}
                  </TextCell>
                ),
              }),
            ]
          : []),
        columnHelper.accessor(getColumnId(translatedColumnNames.road.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.road.label}
            </TextHeader>
          ),
          cell: (info) => (
            <MultiLineCell size={'S'}>{info.getValue() as string}</MultiLineCell>
          ),
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.track.id), {
          header: sortableHeader('track'),
          cell: (info) => (
            <TextCell size={'S'}>{info.getValue() as string}</TextCell>
          ),
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.direction.id), {
          header: sortableHeader('direction'),
          cell: (info) => (
            <TextCell size={'S'}>{info.getValue() as string}</TextCell>
          ),
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.pkInit.id), {
          header: sortableHeader('pkInit'),
          cell: (info) => <PkCell value={info.getValue() as string} />,
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.pkEnd.id), {
          header: sortableHeader('pkEnd'),
          cell: (info) => <PkCell value={info.getValue() as string} />,
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.user.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.user.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCell size={'S'}>{(info.getValue() as string) || ''}</TextCell>
          ),
        }),
        // Sin columna "Registrado por": ese dato solo se pide al crear una
        // Actuación o un Accidente (el desplegable del formulario se renderiza
        // con `isAction || isAccident`), y esta tabla lista órdenes genéricas,
        // campañas y recorridos → salía vacía por construcción. Se coló al
        // añadirla a las cuatro tablas de golpe en 2026-07-23.
        columnHelper.accessor(
          getColumnId(translatedColumnNames.description.id),
          {
            header: () => (
              <TextHeader size={'XS'} colorText={'greyDark'}>
                {translatedColumnNames.description.label}
              </TextHeader>
            ),
            cell: (info) => (
              <TextCell size={'S'}>{info.getValue() as string}</TextCell>
            ),
          }
        ),
        columnHelper.accessor(
          getColumnId(translatedColumnNames.creationDate.id),
          {
            header: sortableHeader('creationDate'),
            cell: (info) => (
              <TextCell size={'S'}>{tableDate(info.getValue())}</TextCell>
            ),
          }
        ),
      ].filter((col: any) => {
        // V4 FR-1.6 — caller puede ocultar columnas por id (e.g. surveillance
        // route excluye Capítulo/Subcapítulo/Tipo vial/Sentido/Predefinidos/
        // Fecha sistema porque no aplican al dominio).
        if (!hiddenColumnIds?.length) return true;
        const colId = (col as any).id ?? (col as any).accessorKey;
        return !hiddenColumnIds.includes(colId);
      }),
    [
      isPortraitTablet,
      sizeTag,
      translatedColumnNames,
      hiddenColumnIds,
      showRouteNameColumn,
      routeNameHeaderLabel,
      showCreatorColumn,
      showOperatorsFilter,
      isSurveillanceTable,
      sortableHeader,
      fixedOrder,
      showFlagColumn,
      getFlagOrderId,
      t,
    ]
  );

  // Tarea #11 — Sincronizar TanStack pageSize con `takePagination` (server).
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize: takePagination,
    }),
    [pageIndex, takePagination]
  );

  const filterFns = {
    roidFilter: roidFilterFunction,
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll as Order[],
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
    },
    filterFns,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true, //enable row selection for all rows
    enableMultiRowSelection: false,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  // commonColumns definition removed

  // commonLabels definition removed

  const capitolOptions = useCallback(() => {
    return data?.filtersDataOptions?.capitols.map((capitol) => ({
      label: capitol.name,
      value: capitol.id,
      subCapitolsIDs: capitol.subCapitolsIDs,
    }));
  }, [data?.filtersDataOptions?.capitols]);

  const concessionOptions = useCallback(() => {
    return data?.filtersDataOptions?.concessions.map((concession) => ({
      label: concession.name,
      value: concession.id,
    }));
  }, [data?.filtersDataOptions?.concessions]);

  const teamOptions = useCallback(() => {
    return data?.filtersDataOptions?.teams?.map(
      (team: { name: string; id: string }) => ({
        label: team.name,
        value: team.id,
      })
    );
  }, [data?.filtersDataOptions?.teams]);

  // FR-1.5 (RV): opciones de Carretera limitadas a la CONCESIÓN seleccionada
  // (o la única, auto-seleccionada) — patrón canónico de las tablas de
  // comunicaciones (InputRadioArrowTable.filterRoadsByConcessionOnMount).
  // Sin concesión seleccionada → todas las carreteras.
  const roadOptions = useMemo(() => {
    const allRoads = (data?.filtersDataOptions?.roads ?? []).map((road) => ({
      label: road.name,
      value: road.id,
    }));
    const concessionId =
      selectorData?.concessionId ?? selectorCursor?.concessionId;
    if (!concessionId) return allRoads;
    const concession = data?.filtersDataOptions?.concessions?.find(
      (c) => c.id === concessionId
    );
    if (!concession?.roadIDs?.length) return allRoads;
    return allRoads.filter((road) => concession.roadIDs?.includes(road.value));
  }, [
    data?.filtersDataOptions?.roads,
    data?.filtersDataOptions?.concessions,
    selectorData?.concessionId,
    selectorCursor?.concessionId,
  ]);

  // Auto-select the only concession if there's only one available
  useEffect(() => {
    const options = concessionOptions();
    if (
      options &&
      options.length === 1 &&
      !selectorCursor.concessionId &&
      !hasAutoSelectedConcession.current
    ) {
      const onlyConcession = options[0];
      hasAutoSelectedConcession.current = true;
      const dataCursor = {
        ...globalCursor,
        concessionId: onlyConcession.value,
      };

      dispatch(setCursor(tableId as string, dataCursor));

      setSelectorCursor((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
      setSelectorData((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
    }
  }, [concessionOptions, selectorCursor.concessionId]);

  const classificationOptions = useMemo(() => {
    return data?.filtersDataOptions?.classifications
      ?.slice()
      ?.sort(
        (a, b) => (a.classificationCode ?? 0) - (b.classificationCode ?? 0)
      )
      ?.map((classification) => ({
        label: `${classification.classificationCode} - ${classification.name}`,
        value: classification.id,
      }));
  }, [data?.filtersDataOptions?.classifications]);

  const predefinedOrderOptions = useMemo(() => {
    return data?.filtersDataOptions?.predefinedOrders?.map(
      (predefined: { id: string; name: string }) => ({
        label: predefined.name,
        value: predefined.id,
      })
    );
  }, [data?.filtersDataOptions?.predefinedOrders]);

  const trackOptions = useMemo(() => getTrackOptions(t), [t]);

  // Campos que el usuario ha VACIADO a mano desde la última búsqueda
  // (2026-10-26). La lupa combina `lo tecleado ?? lo del cursor`, y un campo
  // vacío vale `undefined`: sin esto recuperaba el valor anterior del cursor y,
  // con Expediente en blanco, seguía filtrando por el expediente de antes.
  // Solo los campos de texto que se pueden vaciar y emiten `undefined` al
  // hacerlo; Descripción y Nombre emiten '' y no tienen el problema.
  const clearedFieldsRef = useRef(new Set<string>());
  const markCleared = useCallback((fields: string[], value: unknown) => {
    for (const field of fields) {
      if (value === undefined) clearedFieldsRef.current.add(field);
      else clearedFieldsRef.current.delete(field);
    }
  }, []);

  //I select the column and we pass the filter with the input values
  const onChangeInputExpedient = useCallback<onChangeInputExpedientTypes>(
    (value) => {
      markCleared(['indexExpedition'], value);
      setExpedientDefault(value);
      setSelectorData((prevData) => ({
        ...prevData,
        indexExpedition: value,
      }));
    },
    [markCleared]
  );

  const onChangeInputIndex = useCallback<onChangeInputIndexTypes>(
    (value) => {
      markCleared(['indexType'], value);
      setIndexDefault(value);
      setSelectorData((prevData) => ({
        ...prevData,
        indexType: value,
      }));
    },
    [markCleared]
  );

  const onChangePKInit = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      markCleared(['pkInitKm', 'pkInitMeter'], km);
      setSelectorData((prevData) => ({
        ...prevData,
        pkInitKm: km,
        pkInitMeter: meter,
      }));
    },
    [markCleared]
  );

  const onChangePKEnd = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      markCleared(['pkEndKm', 'pkEndMeter'], km);
      setSelectorData((prevData) => ({
        ...prevData,
        pkEndKm: km,
        pkEndMeter: meter,
      }));
    },
    [markCleared]
  );

  /**
   * *Function for filtering date ranges
   * @param range is a range of dates ({from, to})
   */
  const onChangeInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: fromDateIso,
        toDate: toDateIso,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromDate = fromDateIso;
        newData.toDate = toDateIso;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  /**
   * Rango sobre la fecha de CIERRE (2026-08-11). Hermano de `onChangeInput`,
   * que acota el INICIO.
   *
   * A diferencia de aquel, este NO reconstruye el cursor entero: `setCursor`
   * hace MERGE, así que basta con mandar sus dos claves. Reconstruirlo obligaría
   * a mantener aquí la lista completa de filtros y a acordarse de cada uno que
   * se añada — que es exactamente el olvido que produjo el bug de los filtros
   * mudos que esta rama arregla.
   */
  const onChangeEndDateInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromEndDate = range.from ? formatISO(range.from) : undefined;
      const toEndDate = range.to ? formatISO(range.to) : undefined;
      dispatch(setCursor(tableId as string, { fromEndDate, toEndDate }));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromEndDate = fromEndDate;
        newData.toEndDate = toEndDate;
        return newData;
      });
    },
    [dispatch, tableId]
  );

  const onChangeSelectedCapitolGroup = useCallback(
    async (option: option) => {
      const {
        data: { readSubCapitols },
      } = await client.query({
        query: GET_SUB_CAPITOLS_BY_IDS,
        variables: {
          ids: option.subCapitolsIDs,
        },
        fetchPolicy: 'cache-first',
      });
      const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setSubCapitolOptions(
        sortedSubCapitols?.map((road: { id: string; name: string }) => ({
          value: road.id,
          label: road.name,
        }))
      );
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: undefined,
        capitolId: option.value,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.capitolId = option.value;
        newData.subCapitolId = undefined;
        return newData;
      });

      inputListSubcapitolRef.current?.clearSelected();
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedSubcapitolGroup = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: option.value,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.subCapitolId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedConcession = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId: option.value,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.concessionId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.capitolId,
      selectorData?.teamIds,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedTeam = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextTeamIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: nextTeamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, teamIds: nextTeamIds }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.capitolId,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.teamIds,
      dispatch,
      tableId,
    ]
  );

  const onChangeSelectedStates = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'states']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextStates = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId: selectorData.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: nextStates,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, states: nextStates }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  // Multi-select creador (entidad doble GenericOrder.orders[0].userId).
  // Patrón canónico replicado de AccidentsTable.onChangeSelectedUserIds:
  // ordena estable para no fragmentar la cache Apollo, propaga al cursor
  // Redux + selectorData local sin tocar el resto del estado del cursor.
  const onChangeSelectedUserIds = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextUserIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: nextUserIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, userIds: nextUserIds }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.teamIds,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.includeInactive,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.track,
      dispatch,
      tableId,
    ]
  );

  // Multi-select Operarios (solo RV, gated por `showOperatorsFilter`): filtra
  // por RV.userIDs (operarios del recorrido) vía `operatorIds` del backend.
  // Mismo patrón canónico que onChangeSelectedUserIds (orden estable para no
  // fragmentar la cache Apollo; el cursor Redux hace MERGE del resto).
  const onChangeSelectedOperatorIds = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextOperatorIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: nextOperatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        operatorIds: nextOperatorIds,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.teamIds,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.includeInactive,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.track,
      dispatch,
      tableId,
    ]
  );

  // Toggle "Incluir inactivos en Creador". Solo expande la lista del dropdown;
  // NO filtra los resultados del listado por activo/inactivo.
  const onChangeIncludeInactive = useCallback(
    (next: boolean) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive: next,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, includeInactive: next }));
    },
    [selectorCursor, selectorData, dispatch, tableId]
  );

  // «Solo marcadas» (spec 2026-10-22 § 8.3). Solo guarda en el cursor —el
  // reducer hace merge, así que basta con su clave— y la búsqueda la lanza
  // Buscar. Desmarcado es `undefined`, nunca `false`: así la caché de Apollo no
  // se parte en dos claves para «sin filtro».
  const onChangeOnlyFlagged = useCallback(
    (next: boolean) => {
      dispatch(
        setCursor(tableId as string, { onlyFlagged: next ? true : undefined })
      );
    },
    [dispatch, tableId]
  );

  // Opciones del dropdown Creador. Filtra por roles {SuperAdmin, Coex, RoomTeam,
  // Tablet}. Merge missing ids para que chips de usuarios inactivos previamente
  // marcados sigan renderizando aunque hayan caído del listado base.
  const userOptions = useMemo(() => {
    const base = filterAndSortMultimediaUsers(
      data?.filtersDataOptions?.users ?? [],
      selectorCursor?.includeInactive ?? false
    );
    const selectedIds = selectorCursor?.userIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id)
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filtersDataOptions?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter(
        (
          u: unknown
        ): u is {
          id?: string;
          name?: string;
          surName?: string;
          isActive?: boolean;
        } => Boolean(u)
      )
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id ?? '',
      }));
    return [...base, ...missingOptions];
  }, [
    data?.filtersDataOptions?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.userIds,
  ]);

  // Opciones del dropdown Operarios (solo RV, gated por showOperatorsFilter).
  // Filtra por userType 'operator' (operario de campo sin login) — mismo
  // patrón que ValidationsTable. Merge missing ids para que chips de
  // operarios inactivos previamente marcados sigan renderizando.
  const operatorOptions = useMemo(() => {
    if (!showOperatorsFilter) return [];
    const base = filterAndSortOperators(
      data?.filtersDataOptions?.users ?? [],
      selectorCursor?.includeInactive ?? false
    );
    const selectedIds = selectorCursor?.operatorIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id)
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filtersDataOptions?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter(
        (
          u: unknown
        ): u is {
          id?: string;
          name?: string;
          surName?: string;
          isActive?: boolean;
        } => Boolean(u)
      )
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id ?? '',
      }));
    return [...base, ...missingOptions];
  }, [
    showOperatorsFilter,
    data?.filtersDataOptions?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.operatorIds,
  ]);

  const onClearValue = useCallback(() => {
    inputListClassificationRef.current?.clearSelected();
    inputListCapitolRef.current?.clearSelected();
    inputListSubcapitolRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();
    inputListTrackRef.current?.clearSelected();
    inputListRoadRef.current?.clearSelected();
    inputDateRef.current?.clearSelected();
    inputEndDateRef.current?.clearSelected();
    inputExpedientRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    inputUserRef.current?.clearSelected();
    inputOperatorsRef.current?.clearSelected();
    inputListTeamRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const options = concessionOptions();
    const hasOnlyOneConcession = options && options.length === 1;
    const onlyConcessionId = hasOnlyOneConcession
      ? options[0].value
      : undefined;

    // If there's only one concession, keep it selected
    // Otherwise, clear it
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
      hasAutoSelectedConcession.current = false;
    }

    // Misma lista que el vaciado al abrir un modal (`useFreshTableCursor`).
    const dataCursor = {
      take: takePagination,
      cursor: '',
      ...CLEARED_TABLE_FILTERS,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
    };
    clearedFieldsRef.current.clear();
    dispatch(setCursor(tableId as string, dataCursor));
    setCurrentServerPage(1);
    setSelectorCursor((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      predefinedOrderId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      teamIds: undefined,
      fromDate: undefined,
      toDate: undefined,
      fromEndDate: undefined,
      toEndDate: undefined,
      classification: undefined,
      track: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      operatorIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      routeName: undefined,
      roadId: undefined,
      sortField: undefined,
      orderBy: 'desc',
      onlyFlagged: undefined,
    }));
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      predefinedOrderId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      teamIds: undefined,
      fromDate: undefined,
      toDate: undefined,
      fromEndDate: undefined,
      toEndDate: undefined,
      classification: undefined,
      track: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      operatorIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      routeName: undefined,
      roadId: undefined,
    }));
    setSubCapitolOptions(initialSubCapitolsOptions);
    onReFetchData?.();
    // Tarea #11 fix: `takePagination` debe estar en deps para que `onClearValue`
    // no capture en stale closure el valor del primer render (causaba que tras
    // cambiar el selector 25→100 y clicar papelera se dispatcheara take=25,
    // pero pageCount seguía usando 100 → 25 filas con "1 de 3, total 275").
  }, [
    concessionOptions,
    onChangeInputExpedient,
    onChangeInputIndex,
    takePagination,
  ]);

  const handleClickRow = (row: Row<dataTable>) => {
    const val: boolean = row.getIsSelected();
    if (!val) {
      setRowState(row.original.id || '');
    } else {
      setRowState('');
    }
  };

  // Sync rowSelection → rowState for direct radio button clicks (stopPropagation prevents onSingleTap)
  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (key) => (rowSelection as Record<string, boolean>)[key]
    );
    setRowState(selectedIds.length > 0 ? selectedIds[0] : '');
  }, [rowSelection, setRowState]);

  // La fila elegida ya no está en la lista (un filtro la quitó): se suelta, y
  // el efecto de arriba avisa al consumidor con `setRowState('')`. Ver
  // `selectableRowIds` en types.ts.
  useEffect(() => {
    if (!selectableRowIds) return;
    const valid = new Set(selectableRowIds);
    setRowSelection((prev) => {
      const selected = Object.entries(prev as Record<string, boolean>).filter(
        ([, isOn]) => isOn
      );
      const kept = selected.filter(([id]) => valid.has(id));
      return kept.length === selected.length ? prev : Object.fromEntries(kept);
    });
  }, [selectableRowIds]);

  const fetchOrderID = useCallback(
    async (id: string, take = takePagination) => {
      const dataSelector = {
        take,
        cursor: id,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };

      if (take > 0) {
        const lastGenericOrder =
          dataAll[dataAll.length - 1].genericOrCampaignOrderID;

        if (lastGenericOrder) {
          dispatch(setCursor(tableId as string, dataSelector));
        }
      } else {
        const firstGenericOrder = dataAll[0].genericOrCampaignOrderID;

        if (firstGenericOrder) {
          dispatch(setCursor(tableId as string, dataSelector));
        }
      }

      return dataSelector;
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dataAll,
      takePagination,
    ]
  );
  const nextPagination = useCallback(async () => {
    // Clamp defensivo: aunque backend o cache devuelvan `hasNextPage: true`
    // incorrectamente en la última página real (bug off-by-one conocido en
    // resolvers `length >= take`), nunca avanzamos por encima de pageCount.
    const pageCount = Math.max(
      1,
      Math.ceil((data?.totalCount ?? 0) / takePagination)
    );
    if (
      dataAll.length >= 1 &&
      data?.pageInfo.hasNextPage &&
      currentServerPage < pageCount
    ) {
      setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
      await fetchOrderID(
        dataAll[dataAll.length - 1].genericOrCampaignOrderID as string
      );

      onReFetchPagination?.();
    }
  }, [data, dataAll, fetchOrderID, takePagination, currentServerPage]);

  const backPagination = useCallback(async () => {
    if (dataAll.length >= 1 && data?.pageInfo.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchOrderID(
        dataAll[0].genericOrCampaignOrderID as string,
        -takePagination
      );

      onReFetchPagination?.();
    }
  }, [data, dataAll, fetchOrderID, takePagination]);

  const handleSearch = async () => {
    // Un campo vaciado a mano manda `undefined`, no lo que quedaba en el cursor.
    const unlessCleared = <T,>(field: string, value: T): T | undefined =>
      clearedFieldsRef.current.has(field) ? undefined : value;
    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: unlessCleared(
        'indexType',
        selectorData?.indexType ?? selectorCursor?.indexType
      ),
      indexExpedition: unlessCleared(
        'indexExpedition',
        selectorData?.indexExpedition ?? selectorCursor?.indexExpedition
      ),
      subCapitolId: selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
      capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
      predefinedOrderId:
        selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
      teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      classification:
        selectorData?.classification ?? selectorCursor?.classification,
      states: selectorData?.states ?? selectorCursor?.states,
      userIds: selectorData?.userIds ?? selectorCursor?.userIds,
      operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
      includeInactive:
        selectorData?.includeInactive ?? selectorCursor?.includeInactive,
      pkInitKm: unlessCleared(
        'pkInitKm',
        selectorData?.pkInitKm ?? selectorCursor?.pkInitKm
      ),
      pkInitMeter: unlessCleared(
        'pkInitMeter',
        selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter
      ),
      pkEndKm: unlessCleared(
        'pkEndKm',
        selectorData?.pkEndKm ?? selectorCursor?.pkEndKm
      ),
      pkEndMeter: unlessCleared(
        'pkEndMeter',
        selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter
      ),
      description:
        (selectorData?.description ?? selectorCursor?.description) || undefined,
      track: selectorData?.track ?? selectorCursor?.track,
      routeName:
        (selectorData?.routeName ?? selectorCursor?.routeName) || undefined,
      roadId: selectorData?.roadId ?? selectorCursor?.roadId,
    };
    dispatch(setCursor(tableId as string, dataCursor));
    // Ya están en el cursor como `undefined`: no hace falta recordarlos más.
    clearedFieldsRef.current.clear();
    setCurrentServerPage(1);
    onReFetchData?.();
  };

  const onChangeSelectedClassification = useCallback(
    (value: OptionSelected) => {
      let isSame = false;
      if (selectorData.classification !== value.value) {
        setSelectorData({
          ...selectorData,
          classification: value.value,
        });

        const setCursorData = {
          ...selectorCursor,
          classification: value.value,
        };

        dispatch(setCursor(tableId as string, setCursorData));
      } else {
        isSame = true;
      }
      return isSame;
    },
    [selectorData, selectorCursor, dispatch, tableId]
  );

  const onChangeSelectedTrack = useCallback((value: OptionSelected) => {
    setSelectorData((prevData) => ({
      ...prevData,
      track: value.value,
    }));
  }, []);

  const onChangeSelectedPredefinedOrder = useCallback(
    (value: OptionSelected) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId: value.value,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        predefinedOrderId: value.value,
      }));
    },
    [selectorData, selectorCursor, dispatch, tableId]
  );

  // Columnas que ofrece el desplegable «Columnas»: todas menos la de selección
  // (la primera) y las que no se pueden ocultar (la bandera, § 8.2). Sin
  // bandera queda igual que el `.slice(1)` de siempre.
  const hideableColumns = table
    .getAllColumns()
    .slice(1)
    .filter((column) => column.getCanHide());

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(
    filterContainerRef,
    handleSearch,
    onClearValue,
    isLoadingData as boolean
  );

  return (
    <ContainerStyled>
      {isLoadingData && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersWithLoadingStyled isLoading={isLoadingData as boolean}>
        <SubContainerHead>
          <Title component={'h2'} variant={'semiBold'} size={'M'}>
            {titleText}
          </Title>
          {isLanscapeTablet && !hideFilterBar && (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters} />
            </ShowFiltersButton>
          )}
        </SubContainerHead>
        {subtitle && <SubtitleStyled>{subtitle}</SubtitleStyled>}
        {showFilters && !hideFilterBar && (
          <SubContainerFiltersStyled ref={filterContainerRef}>
            {!isFilterHidden('expedient') && (
              <InputExpedient
                label={t('headers.expedient')}
                disabled={isLoadingData}
                expedient={expedientDefault}
                onChangeInputExpedient={onChangeInputExpedient}
                ref={inputExpedientRef}
              />
            )}

            {!isFilterHidden('index') && (
              <InputIndex
                label={t('headers.index')}
                disabled={isLoadingData}
                index={indexDefault}
                onChangeInputIndex={onChangeInputIndex}
                ref={inputIndexRef}
              />
            )}

            {/* FR-1.5 (RV): filtro por Nombre del recorrido — mismo gate que
                la columna (showRouteNameColumn, solo lo activa el RV). El
                backend ya soporta `name` (contains, case-insensitive). Label
                corto "Nombre" (feedback usuario: el largo parte en 2 líneas
                y descuelga el input de la fila de filtros).

                2026-08-11: gate propio `showRouteNameFilter`, que por defecto
                sigue a la columna. Los separa el modal «Vincular tramo» abierto
                desde un recorrido: allí la columna informa y el filtro no
                podría quitar ni una fila. */}
            {routeNameFilterVisible && (
              <InputText
                label={t('common:labels.name')}
                typeInput="normal"
                inputSize="S"
                placeholder={t('common:placeholders.writeHere')}
                disabled={isLoadingData}
                value={
                  selectorData?.routeName ?? selectorCursor?.routeName ?? ''
                }
                onChange={(e) =>
                  setSelectorData((prev) => ({
                    ...prev,
                    routeName: e.target.value,
                  }))
                }
              />
            )}

            {/* FR-1.5 (RV): filtro por Carretera — el loader del RV es el
                único que provee `roads` en filtersDataOptions. Opciones
                limitadas a la concesión seleccionada (roadOptions). */}
            {showRouteNameColumn && roadOptions.length > 0 && (
              <SearchableDropdownStyledBig
                ref={inputListRoadRef}
                inputSize="S"
                typeDropDown="Default"
                labelText={t('tables:headers.road')}
                placeholder={t('common:placeholders.select')}
                options={roadOptions}
                selectedOption={
                  selectorData?.roadId ?? selectorCursor?.roadId ?? ''
                }
                onChangeSelected={(option) =>
                  setSelectorData((prev) => ({
                    ...prev,
                    roadId: option?.value || undefined,
                  }))
                }
                isDisabled={isLoadingData}
              />
            )}

            {!isFilterHidden('pkInit') && (
              <InputPK
                label={t('tables:headers.pkInit')}
                disabled={isLoadingData}
                onChangeInputPK={onChangePKInit}
                ref={inputPKInitRef}
              />
            )}

            {!isFilterHidden('pkEnd') && (
              <InputPK
                label={t('tables:headers.pkEnd')}
                disabled={isLoadingData}
                onChangeInputPK={onChangePKEnd}
                ref={inputPKEndRef}
              />
            )}
            {/* Filtro por descripción: texto libre sobre Order.description.
                Busca por subcadena, sin distinguir acentos ni mayúsculas, y
                exige todas las palabras en cualquier orden (el backend filtra
                contra la copia normalizada `descriptionSearch`, indexada).

                Guard `isFilterHidden` añadido 2026-08-10: era el ÚNICO filtro
                del bloque que se pintaba incondicionalmente, así que un
                consumidor cuyo endpoint no acepta el criterio —el modal de
                vincular tramos, cuyo `FilterPendingExternalSegments` solo
                admite recorrido y rango de fechas— no tenía forma de ocultarlo
                y mostraba un input inerte. Sus vecinos pkInit/pkEnd ya se
                ocultaban ahí por esa misma razón. */}
            {!isFilterHidden('description') && (
              <InputText
                label={t('tables:headers.description')}
                typeInput="normal"
                inputSize="S"
                placeholder={t('common:placeholders.writeHere')}
                disabled={isLoadingData}
                value={
                  selectorData?.description ?? selectorCursor?.description ?? ''
                }
                onChange={(e) =>
                  setSelectorData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            )}
            {!isFilterHidden('date') && (
              <InputCalendarStyled
                ref={inputDateRef}
                label={t('tables:filters.dateRangeFilter')}
                name={'data'}
                onChangeInput={onChangeInput}
                dateRange={
                  selectorCursor?.fromDate && selectorCursor?.toDate
                    ? {
                        from: parseISO(selectorCursor?.fromDate),
                        to: parseISO(selectorCursor?.toDate),
                      }
                    : undefined
                }
                disabled={isLoadingData}
              />
            )}
            {/* Segundo rango: la fecha de CIERRE del tramo (2026-08-11).
                Opt-in — solo lo activa quien lista entidades que tienen inicio
                Y fin y necesita acotar por los dos. Reusa la etiqueta que ya
                nombra esa columna, sin claves i18n nuevas. */}
            {showEndDateFilter && (
              <InputCalendarStyled
                ref={inputEndDateRef}
                label={t('forms:workOrder.endDate')}
                name={'dataFi'}
                onChangeInput={onChangeEndDateInput}
                dateRange={
                  selectorCursor?.fromEndDate && selectorCursor?.toEndDate
                    ? {
                        from: parseISO(selectorCursor?.fromEndDate),
                        to: parseISO(selectorCursor?.toEndDate),
                      }
                    : undefined
                }
                disabled={isLoadingData}
              />
            )}
            {!isFilterHidden('concession') &&
              concessionOptions() &&
              (concessionOptions()?.length ?? 0) > 1 && (
                <DropDownInput
                  inputSize="XS"
                  ref={inputListConRef}
                  labelText={t('tables:headers.concession')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Secondary'}
                  defaultValue={selectorCursor?.concessionId ?? ''}
                  options={concessionOptions() as Options}
                  onChangeSelected={onChangeSelectedConcession as onChangeValue}
                  isDisabled={isLoadingData}
                />
              )}
            {!isFilterHidden('team') &&
              teamOptions() &&
              (teamOptions()?.length ?? 0) > 0 && (
                <MultiSelectDropdown
                  inputSize="M"
                  ref={inputListTeamRef}
                  labelText={t('forms:workOrder.team')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Secondary'}
                  selectedValues={selectorCursor?.teamIds ?? []}
                  options={teamOptions() as Options}
                  onChangeSelected={onChangeSelectedTeam}
                  isDisabled={isLoadingData}
                />
              )}
            {!isFilterHidden('classification') && (
              <SearchableDropdownStyledBig
                inputSize="L"
                ref={inputListClassificationRef}
                labelText={t('tables:headers.classification')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.classification ?? ''}
                selectedOption={selectorCursor?.classification ?? undefined}
                options={classificationOptions as Options}
                onChangeSelected={
                  onChangeSelectedClassification as onChangeValue
                }
                isDisabled={isLoadingData}
              />
            )}
            {!isFilterHidden('track') && (
              <SearchableDropdownStyledBig
                inputSize="L"
                ref={inputListTrackRef}
                labelText={t('forms:workOrder.roadType')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.track ?? ''}
                options={trackOptions as Options}
                onChangeSelected={onChangeSelectedTrack as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            {!isFilterHidden('predefinedOrder') &&
              showPredefinedFilter &&
              predefinedOrderOptions &&
              predefinedOrderOptions.length > 0 && (
                <SearchableDropdownStyledBig
                  inputSize="L"
                  ref={inputListPredefinedRef}
                  labelText={t('forms:workOrder.predefined')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Default'}
                  defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                  options={predefinedOrderOptions as Options}
                  onChangeSelected={
                    onChangeSelectedPredefinedOrder as onChangeValue
                  }
                  isDisabled={isLoadingData}
                />
              )}
            {!isFilterHidden('capitol') && showCapitolFilter && (
              <SearchableDropdownStyledBig
                inputSize="L"
                ref={inputListCapitolRef}
                labelText={'Capitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.capitolId ?? ''}
                options={capitolOptions() as Options}
                onChangeSelected={onChangeSelectedCapitolGroup as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            {!isFilterHidden('subCapitol') && showSubCapitolFilter && (
              <SearchableDropdownStyledBig
                inputSize="L"
                ref={inputListSubcapitolRef}
                labelText={'Subcapitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.subCapitolId ?? ''}
                options={subCapitolOptions as Options}
                onChangeSelected={
                  onChangeSelectedSubcapitolGroup as onChangeValue
                }
                isDisabled={isLoadingData}
              />
            )}
            {!isFilterHidden('state') && (
              <MultiSelectDropdown
                inputSize="S"
                ref={inputStateRef}
                labelText={t('tables:headers.status')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                selectedValues={selectorCursor?.states ?? []}
                options={
                  roleActive
                    ? getStateOptionsByRoleAndEntityType(
                        t,
                        roleActive,
                        TABLE_ENTITY_TYPES[tableId as string],
                        listingFiltersByRole(tableId)
                      ).filter(
                        (option) =>
                          !excludedStateOptions?.includes(option.value)
                      )
                    : []
                }
                onChangeSelected={onChangeSelectedStates}
                isDisabled={isLoadingData}
              />
            )}
            {!isFilterHidden('creator') &&
              userOptions &&
              userOptions.length > 0 && (
                <MultiSelectDropdownStyled
                  inputSize="L"
                  ref={inputUserRef}
                  labelText={t('common:labels.creator')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Default'}
                  searchable
                  selectedValues={selectorCursor?.userIds ?? []}
                  options={userOptions}
                  onChangeSelected={onChangeSelectedUserIds}
                  isDisabled={isLoadingData}
                />
              )}
            {/* Filtro Operarios (solo RV): filtra por RV.userIDs — mismo
                patrón multi-select que Creador/Operarios de Actuaciones. */}
            {showOperatorsFilter && operatorOptions.length > 0 && (
              <MultiSelectDropdownStyled
                inputSize="L"
                ref={inputOperatorsRef}
                labelText={t('forms:workOrder.operators')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                searchable
                selectedValues={selectorCursor?.operatorIds ?? []}
                options={operatorOptions}
                onChangeSelected={onChangeSelectedOperatorIds}
                isDisabled={isLoadingData}
              />
            )}
            <ContainerButtonStyled>
              <ButtonIconBackground
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoadingData}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumn
                columnNames={Object.fromEntries(
                  hideableColumns.map((c) => [
                    c.id,
                    translatedColumnNames[
                      c.id as keyof typeof translatedColumnNames
                    ]?.label ||
                      (c.id === 'operators'
                        ? t('forms:workOrder.operators')
                        : c.id === 'name'
                          ? t('pages:surveillance.name')
                          : c.id === 'creator'
                            ? t('common:labels.creator')
                            : c.id),
                  ])
                )}
                columns={hideableColumns}
                isDisabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'restart'}
                onClick={() => {
                  setCurrentServerPage(1);
                  (onReloadData ?? onReFetchData)?.();
                }}
                disabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoadingData}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtonStyled>
            {/* UN solo toggle, etiqueta según qué desplegables se vean — mismo
                patrón que `ValidationsTable`, que es como lo resuelven 6 de las
                8 tablas de la casa.
       
                Antes había DOS casillas ("…en Creador" y "…en Operarios") atadas
                al MISMO `includeInactive` del cursor: marcar una marcaba la otra
                y se leían como si fueran independientes. Aparecieron así porque
                el toggle de Creador es anterior a que Ronda 3 añadiera el
                desplegable de Operarios al RV; en vez de pasar a la etiqueta
                combinada se clonó el bloque (y el clon acabó también en
                `CheckBoxTagTable`).
       
                Un solo flag es lo correcto, no un apaño: los dos desplegables
                se alimentan del MISMO `filtersDataOptions.users` y solo cambia
                la rebanada (`filterAndSortMultimediaUsers` para creadores,
                `filterAndSortOperators` para operarios de campo). Nadie quiere
                ver inactivos en uno y ocultarlos en el otro.
       
                Cuando solo se ve un desplegable la etiqueta lo dice (en el RV
                la pestaña Actuaciones oculta Operarios, porque van ligados al
                recorrido). Las tres claves ya existen en ca/es/en. */}
            {(() => {
              const verCreador =
                !isFilterHidden('creator') &&
                !!userOptions &&
                userOptions.length > 0;
              const verOperarios =
                showOperatorsFilter && operatorOptions.length > 0;
              if (!verCreador && !verOperarios) return null;
              const etiqueta =
                verCreador && verOperarios
                  ? 'common:filters.includeInactiveInCreatorAndOperators'
                  : verCreador
                    ? 'common:filters.includeInactiveInCreator'
                    : 'common:filters.includeInactiveInOperators';
              return (
                <IncludeInactiveToggle
                  checked={selectorCursor?.includeInactive ?? false}
                  onChange={onChangeIncludeInactive}
                  label={t(etiqueta)}
                  isDisabled={isLoadingData}
                />
              );
            })()}
            {showOnlyFlaggedFilter && (
              <IncludeInactiveToggle
                checked={Boolean(selectorCursor?.onlyFlagged)}
                onChange={onChangeOnlyFlagged}
                label={flagScopeTexts.onlyFlagged}
                isDisabled={isLoadingData}
              />
            )}
          </SubContainerFiltersStyled>
        )}
      </FiltersWithLoadingStyled>
      <TableContainerStyled isLoading={isLoadingData as boolean}>
        <Table {...props}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    {...exportIgnoreProps(header.column.id)}
                    aria-sort={
                      header.column.id === activeSortColumnId
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <DoubleTapRow
                  key={row.id}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                  isDisabled={isLoadingData as boolean}
                  onSingleTap={() => {
                    handleClickRow(row);
                    row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={() => {
                    if (!row.original?.id) return;
                    // Tabla routing-agnóstica (2026-07-09): el doble-tap lo
                    // define SIEMPRE el consumidor vía `onRowDoubleTap`. Antes
                    // esta tabla llamaba `useNavigate()` internamente para el
                    // default `/work-orders/:id`, lo que la obligaba a vivir
                    // dentro de un <Router> y rompía su uso en modales
                    // (RelateSegmentModal renderiza FUERA del árbol de React
                    // Router → necesitaba un MemoryRouter de parche). Ahora las
                    // páginas ruteadas (WorkOrders/DuplicateOrder/
                    // TimeManagement) pasan su propio navigate a
                    // /work-orders/:id; SR navega a /surveillance-route/:id; el
                    // modal pasa seleccionar+confirmar. Sin `onRowDoubleTap`,
                    // doble-tap es no-op (no ocurre: todos lo pasan).
                    onRowDoubleTap?.(row);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TdStyled
                      column={cell.column.id as columnID}
                      hiddenColumns={hiddenColumns}
                      key={cell.id}
                      {...exportIgnoreProps(cell.column.id)}
                    >
                      <>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </>
                    </TdStyled>
                  ))}
                </DoubleTapRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainerStyled>
      {showButtonsPagination && (
        <PaginationButtons
          pageCount={Math.max(
            1,
            Math.ceil((data?.totalCount ?? 0) / takePagination)
          )}
          currentPage={currentServerPage}
          totalCount={data?.totalCount ?? 0}
          nextPage={nextPagination}
          previousPage={backPagination}
          isDisabled={isLoadingData as boolean}
          // Tarea #11: gates aplicados en `useServerPageSize`. En contextos modal
          // o stub (RV preview), los 3 props van como undefined.
          {...paginationProps}
        />
      )}
    </ContainerStyled>
  );
};
